import { useQuery } from "@tanstack/react-query";
import { CheckCircle2, Eraser, ExternalLink, PenLine } from "lucide-react";
import { useEffect, useRef, useState, type PointerEvent } from "react";
import { useParams } from "react-router-dom";
import { fetchSignRequest, submitSignature } from "../api";
import { PoweredByStatic } from "../components/PoweredBy";
import { translate, type Locale } from "../i18n";
import { applyBrand, formatPhone } from "../theme";

/**
 * The signing page. No login: the token in the URL is the key. The customer reads the document, types their name,
 * draws a signature with a finger or mouse, agrees to sign electronically, and signs. Works on the agent's screen in
 * the office and on the customer's phone from a texted link.
 */
export function SignScreen() {
  const { token = "" } = useParams();
  const q = useQuery({ queryKey: ["sign", token], queryFn: () => fetchSignRequest(token), retry: false });
  const [locale, setLocale] = useState<Locale>(() => (navigator.language.toLowerCase().startsWith("es") ? "es" : "en"));
  const t = (k: string) => translate(locale, k);
  const [name, setName] = useState("");
  const [agree, setAgree] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState<{ status: "signed" | "declined"; signedUrl?: string } | null>(null);
  const canvas = useRef<HTMLCanvasElement>(null);
  const drawing = useRef(false);
  const [hasInk, setHasInk] = useState(false);
  const d = q.data;

  useEffect(() => {
    if (d) { applyBrand(d.agency.brand); document.title = `${d.agency.displayName} · ${t("sign.title")}`; setLocale(d.locale); setName((n) => n || d.signerName); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [d?.request.id]);
  useEffect(() => { document.documentElement.lang = locale; }, [locale]);
  useEffect(() => {
    const c = canvas.current;
    if (!c) return;
    const ratio = window.devicePixelRatio || 1;
    const w = c.clientWidth, h = 200;
    c.width = w * ratio; c.height = h * ratio;
    const g = c.getContext("2d");
    if (g) { g.scale(ratio, ratio); g.fillStyle = "#fff"; g.fillRect(0, 0, w, h); g.lineWidth = 2.5; g.lineCap = "round"; g.lineJoin = "round"; g.strokeStyle = "#111"; }
  }, [d?.request.id]);

  const pos = (e: PointerEvent<HTMLCanvasElement>) => { const r = canvas.current!.getBoundingClientRect(); return { x: e.clientX - r.left, y: e.clientY - r.top }; };
  const down = (e: PointerEvent<HTMLCanvasElement>) => { drawing.current = true; canvas.current!.setPointerCapture(e.pointerId); const g = canvas.current!.getContext("2d")!; const p = pos(e); g.beginPath(); g.moveTo(p.x, p.y); };
  const move = (e: PointerEvent<HTMLCanvasElement>) => { if (!drawing.current) return; const g = canvas.current!.getContext("2d")!; const p = pos(e); g.lineTo(p.x, p.y); g.stroke(); setHasInk(true); };
  const up = () => { drawing.current = false; };
  const clear = () => { const c = canvas.current!; const g = c.getContext("2d")!; g.fillStyle = "#fff"; g.fillRect(0, 0, c.clientWidth, 200); setHasInk(false); };

  const sign = async () => {
    if (!canvas.current || !hasInk || !agree || !name.trim()) return;
    setBusy(true); setError("");
    try {
      const png = canvas.current.toDataURL("image/png").split(",")[1]!;
      const r = await submitSignature(token, { signerName: name.trim(), signaturePng: png, agree: true, locale });
      setResult({ status: r.status, signedUrl: r.signedUrl });
    } catch (err) { setError((err as Error).message); }
    finally { setBusy(false); }
  };
  const decline = async () => {
    setBusy(true); setError("");
    try { const r = await submitSignature(token, { signerName: name, signaturePng: "", agree: false, locale, decline: true }); setResult({ status: r.status }); }
    catch (err) { setError((err as Error).message); }
    finally { setBusy(false); }
  };

  const field = "w-full min-h-[52px] rounded-2xl border border-ink/15 bg-white px-4 text-base text-ink placeholder:text-ink/40 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30";
  const button = "inline-flex min-h-[52px] items-center justify-center gap-2 rounded-2xl bg-primary px-5 text-base font-bold text-white shadow-sm hover:bg-primary-dark disabled:opacity-50";
  const status = d?.request.status;
  const done = result ?? (status === "signed" ? { status: "signed" as const, signedUrl: d?.documentUrl ?? undefined } : status === "declined" ? { status: "declined" as const } : null);

  return (
    <div className="min-h-screen bg-surface text-ink font-body">
      <header className="bg-ink text-white">
        <div className="mx-auto flex max-w-2xl items-center justify-between gap-3 px-4 py-3">
          <div><p className="font-bold leading-tight">{d?.agency.displayName ?? "…"}</p><p className="text-[11px] text-white/70">{t("sign.title")}</p></div>
          <div className="flex overflow-hidden rounded-lg border border-white/30 text-xs font-bold">
            {(["en", "es"] as const).map((l) => <button key={l} type="button" onClick={() => setLocale(l)} className={`px-3 py-1.5 ${locale === l ? "bg-white text-ink" : "text-white"}`}>{l.toUpperCase()}</button>)}
          </div>
        </div>
      </header>
      <main className="mx-auto max-w-2xl space-y-4 px-4 py-5">
        {q.isError ? <p className="rounded-2xl bg-red-50 p-4 text-red-800">{t("sign.invalid")}</p> : null}
        {d ? (
          <>
            <section className="rounded-3xl bg-white p-4 shadow-card">
              <h1 className="text-xl font-bold">{d.request.title}</h1>
              <p className="text-sm text-ink/60">{d.signerName} · {formatPhone(d.agency.contact.phone)}</p>
              {d.documentUrl ? <a href={d.documentUrl} target="_blank" rel="noopener" className="mt-3 inline-flex min-h-[44px] items-center gap-2 rounded-2xl border border-ink/15 px-4 text-sm font-semibold hover:border-primary"><ExternalLink className="h-4 w-4" />{done?.status === "signed" ? t("sign.download") : t("sign.open")}</a> : null}
              {d.documentUrl && !done ? <iframe title={d.request.title} src={d.documentUrl} className="mt-3 h-[420px] w-full rounded-2xl border border-ink/10 bg-white" /> : null}
            </section>
            {done ? (
              <section className="rounded-3xl bg-white p-4 shadow-card">
                <p className="flex items-center gap-2 font-semibold text-primary-dark"><CheckCircle2 className="h-5 w-5" />{done.status === "signed" ? t("sign.signed") : t("sign.declined")}</p>
                {done.status === "signed" && done.signedUrl ? <a href={done.signedUrl} target="_blank" rel="noopener" className={`mt-3 ${button}`}><ExternalLink className="h-5 w-5" />{t("sign.download")}</a> : null}
              </section>
            ) : status === "expired" ? <p className="rounded-2xl bg-amber-50 p-4 text-amber-900">{t("sign.expired")}</p> : status !== "pending" ? <p className="rounded-2xl bg-amber-50 p-4 text-amber-900">{t("sign.unavailable")}</p> : (
              <section className="rounded-3xl bg-white p-4 shadow-card space-y-3">
                <p className="text-sm text-ink/70">{t("sign.review")}</p>
                <input className={field} placeholder={t("sign.name")} value={name} onChange={(e) => setName(e.target.value)} aria-label={t("sign.name")} />
                <div>
                  <div className="flex items-center justify-between"><p className="text-sm font-semibold">{t("sign.draw")}</p><button type="button" onClick={clear} className="inline-flex items-center gap-1 text-sm text-ink/60 hover:text-ink"><Eraser className="h-4 w-4" />{t("sign.clear")}</button></div>
                  <canvas ref={canvas} className="mt-1 h-[200px] w-full touch-none rounded-2xl border-2 border-dashed border-ink/25 bg-white" onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerLeave={up} onPointerCancel={up} />
                </div>
                <label className="flex items-start gap-3 text-sm"><input type="checkbox" className="mt-1 h-5 w-5" checked={agree} onChange={(e) => setAgree(e.target.checked)} /><span><span className="font-semibold">{t("sign.agree")}</span><br /><span className="text-ink/60">{locale === "es" ? d.consent.es : d.consent.en}</span></span></label>
                <div className="flex flex-wrap gap-2">
                  <button type="button" onClick={sign} disabled={busy || !hasInk || !agree || !name.trim()} className={button}><PenLine className="h-5 w-5" />{t("sign.submit")}</button>
                  <button type="button" onClick={decline} disabled={busy} className="inline-flex min-h-[52px] items-center rounded-2xl border border-ink/15 px-4 text-sm font-semibold text-ink/70 hover:border-red-300 hover:text-red-700">{t("sign.decline")}</button>
                </div>
              </section>
            )}
            {error ? <p className="rounded-2xl bg-red-50 p-3 text-sm text-red-800">{error}</p> : null}
          </>
        ) : null}
        <PoweredByStatic locale={locale} className="mx-auto mt-6" />
      </main>
    </div>
  );
}
